import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Search, BookOpen, Video, Clock, ChevronRight } from 'lucide-react';
import { getBatchTypeLabel, getCourseTypeLabel, formatDate, formatTime, getInitials, toISTDateStr } from '../../lib/utils';
import { toast } from 'sonner';

export default function InstructorBatches() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchSessions, setBatchSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  useEffect(() => {
    api.get('/api/batches', { params: { search, limit: 50 } })
      .then(res => setBatches(res.data.batches))
      .finally(() => setLoading(false));
  }, [search]);

  const openBatchSessions = async (batch) => {
    setSelectedBatch(batch);
    setSessionsLoading(true);
    try {
      const res = await api.get('/api/sessions', { params: { batch_id: batch.id, status: 'upcoming', limit: 50 } });
      // V14.1 — Interpretation A: upcoming sessions sorted ascending (next class first)
      const sorted = (res.data.sessions || []).slice().sort(
        (a, b) => (a.scheduled_date + a.scheduled_time).localeCompare(b.scheduled_date + b.scheduled_time)
      );
      setBatchSessions(sorted);
    } catch { toast.error('Failed to load sessions'); }
    setSessionsLoading(false);
  };

  const handleStartSession = async (sessionId) => {
    try {
      const res = await api.get(`/api/sessions/${sessionId}/link`);
      if (res.data.zoom_start_url) {
        window.open(res.data.zoom_start_url, '_blank');
        toast.info('Opening Zoom...');
      } else {
        toast.error(res.data.start_blocked_reason || 'Cannot start yet');
      }
    } catch { toast.error('Failed to get session link'); }
  };

  const groupCount = batches.filter(b => b.batch_type === 'group').length;
  const oneOnOneCount = batches.filter(b => b.batch_type === 'one_on_one').length;

  return (
    <div className="space-y-6" data-testid="instructor-batches-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">My Batches</h1>
        <p className="text-muted-foreground mt-1">{groupCount} group · {oneOnOneCount} one-on-one</p>
      </div>

      <Card className="rounded-2xl">
        <CardContent className="pt-5">
          <div className="mb-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search batches..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 max-w-sm" data-testid="instructor-batch-search" />
          </div>

          {batches.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No batches assigned to you</p>
            </div>
          ) : (
            <div className="space-y-2">
              {batches.map(b => (
                <div
                  key={b.id}
                  className="flex items-center justify-between p-4 rounded-xl border hover:bg-muted/20 transition-colors cursor-pointer"
                  onClick={() => openBatchSessions(b)}
                  data-testid={`batch-row-${b.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <BookOpen className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{b.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-[10px]">{getBatchTypeLabel(b.batch_type)}</Badge>
                        <Badge variant="secondary" className="text-[10px]">{getCourseTypeLabel(b.course_type)}</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      {b.next_session ? (
                        <p className="text-xs text-muted-foreground">Next: {formatDate(b.next_session.scheduled_date)} {formatTime(b.next_session.scheduled_time)}</p>
                      ) : (
                        <p className="text-xs text-muted-foreground">No upcoming sessions</p>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Batch Sessions Modal */}
      <Dialog open={!!selectedBatch} onOpenChange={(o) => { if (!o) setSelectedBatch(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">{selectedBatch?.name}</DialogTitle>
            <p className="text-sm text-muted-foreground">{getCourseTypeLabel(selectedBatch?.course_type)} · {getBatchTypeLabel(selectedBatch?.batch_type)}</p>
          </DialogHeader>
          {sessionsLoading ? (
            <div className="flex items-center justify-center py-8"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : batchSessions.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No upcoming sessions</p>
            </div>
          ) : (
            <div className="space-y-2">
              {batchSessions.map(s => {
                const sessionDt = new Date(`${s.scheduled_date}T${s.scheduled_time}:00`);
                const now = new Date();
                const minutesUntil = (sessionDt - now) / (1000 * 60);
                const canStart = minutesUntil <= 10;
                const canShowSoon = minutesUntil <= 20;
                return (
                  <div key={s.id} className="flex items-center justify-between p-3 rounded-xl border">
                    <div className="flex items-center gap-3">
                      <div className="text-center bg-primary/10 rounded-lg p-2 min-w-[40px]">
                        <p className="text-[9px] text-primary uppercase">{new Date(s.scheduled_date + 'T00:00:00').toLocaleDateString('en', { month: 'short' })}</p>
                        <p className="text-lg font-bold text-primary leading-tight">{new Date(s.scheduled_date + 'T00:00:00').getDate()}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">{s.title}</p>
                        <p className="text-xs text-muted-foreground">{formatTime(s.scheduled_time)} · {s.duration_minutes}m</p>
                        <Badge variant="outline" className={`text-[9px] mt-0.5 ${s.status === 'live' ? 'bg-emerald-50 text-emerald-700' : ''}`}>
                          {s.status === 'live' ? 'Live' : s.status}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      {(s.status === 'live' || (s.status === 'upcoming' && canStart)) ? (
                        <Button size="sm" onClick={() => handleStartSession(s.id)} data-testid={`start-session-${s.id}`}>
                          <Video className="h-3.5 w-3.5 mr-1" />{s.status === 'live' ? 'Rejoin' : 'Start'}
                        </Button>
                      ) : canShowSoon ? (
                        <Button size="sm" disabled className="opacity-40">Starting soon</Button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
